// Hover cards for the maps. 2026-10-12, the user asked: "can we have much
// better info showing up when hovering?". The facts are built in one place, in the same words as the Projects view
// (config/displaySchema.js), so the search map, the results list, the
// Projects map and a saved site never disagree.
//
// The fact builders are pure functions of plain objects: a search result,
// a saved project's enrichment, or a substation's or parcel's feature
// properties. Mapbox hands hover features over with nested objects turned
// into strings, so a site card reads the full result object, looked up by
// index, never the hovered feature.
import React from 'react';
import { capacityAge } from '../../config/displaySchema';
import { planCodeName, planCodeSuitability } from '../../utils/planCodes';
import { formatDistanceKm, formatAreaHa, nearestHomeText } from '../../utils/format';
import './HoverCards.css';

const KIND = { QUAY: 'Quay', HARBOUR: 'Harbour', PORT_FACILITY: 'Port facility', REGISTER: 'Port' };

export const num = (v) => {
  if (v == null || v === '' || v === 'null') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const isTrue = (v) => v === true || v === 'true';
const mw = (v) => (num(v) == null ? null : `${Math.round(num(v))} MW`);
const km = (v) => formatDistanceKm(num(v));

// ── zoning, shared by sites and parcels ──────────────────────────────────
export function zoningFact(code, name, suitability) {
  const c = code == null || code === '' || code === 'null' ? null : String(code);
  const n = name || (c ? planCodeName(c) : null);
  if (!n && !c) return null;
  const suit = num(suitability) ?? (c ? planCodeSuitability(c) : null);
  const fit = suit == null ? null
    : suit >= 0.75 ? 'fits a plant' : suit >= 0.4 ? 'check the plan provisions' : 'needs rezoning';
  return { label: 'Zoning', value: `${n || 'Plan code'}${c ? ` (${c})` : ''}`, note: fit, warn: suit != null && suit < 0.75 };
}

// ── sites: search results and saved projects ─────────────────────────────
export function siteTitle(site) {
  if (site?.site_label) return site.site_label;
  const kommune = site?.kommune || site?.municipality_name || site?.municipality;
  return [kommune, site?.matrikkelnummer].filter(Boolean).join(' ') || 'Site';
}

export function siteFacts(site) {
  if (!site) return [];
  const out = [];
  const z = zoningFact(site.zoning_code ?? site.zone_code, site.zone_name || site.zoning_name, site.zone_suitability);
  if (z) out.push(z);

  const hs = site.harbour_screen && typeof site.harbour_screen === 'object' ? site.harbour_screen : null;
  if (hs) {
    const gp = hs.grid_point || {};
    const tests = site.screen_tests_passed;
    const failing = Array.isArray(tests) && tests.length === 0;
    const age = capacityAge(gp);
    const q = num(gp.queue_mw);
    const where = [
      gp.name ? `${gp.name}${gp.owner ? ` (${gp.owner})` : ''}` : null,
      mw(gp.available_mw) ? `${mw(gp.available_mw)} free` : null,
      q == null ? null : q === 0 ? 'no queue' : `${Math.round(q)} MW queue`,
      km(gp.distance_km),
    ].filter(Boolean).join(' · ');
    const tso = site.tso_constraint;
    out.push(tso ? {
      label: 'Capacity',
      value: 'Fails: Statnett restriction',
      note: tso.short,
      warn: true,
    } : {
      label: 'Capacity',
      value: !Array.isArray(tests) ? 'Passes the screen'
        : failing ? 'No test passed on the latest figures' : `Passes ${tests.join(', ')}`,
      note: [where, age.text].filter(Boolean).join(' · '),
      // the test result turns amber only when it fails; old figures colour
      // the line that carries the date
      warn: failing,
      noteWarn: age.stale,
    });
    const hb = hs.harbour || {};
    if (hb.name || hb.distance_km != null) {
      const calls = num(hb.calls);
      out.push({
        label: 'Harbour',
        value: [`${KIND[hb.kind] || 'Harbour'} ${hb.name || ''}`.trim(), km(hb.distance_km)].filter(Boolean).join(' · '),
        note: calls != null ? `${calls} calls ${hb.calls_year ? `in ${hb.calls_year}` : 'last year'}` : null,
      });
    }
    const home = nearestHomeText(hs.nearest_home_m, hs.limits?.min_neighbour_m);
    out.push({ label: 'Homes', value: home.atLimit || hs.nearest_home_m == null ? home.text.toLowerCase() : `nearest ${km(num(hs.nearest_home_m) / 1000)}` });
  } else {
    if (site.nearest_grid_name || site.nearest_grid_distance_km != null) {
      const kv = num(site.nearest_grid_voltage_kv);
      const free = mw(site.nearest_grid_available_mw);
      out.push({
        label: 'Grid',
        value: [site.nearest_grid_name, kv ? `${kv} kV` : null, km(site.nearest_grid_distance_km)].filter(Boolean).join(' · '),
        note: free ? `${free} free${site.nearest_grid_capacity_source ? ` · ${site.nearest_grid_capacity_source}` : ''}` : null,
      });
    }
    if (site.nearest_port_name || site.nearest_port_distance_km != null) {
      out.push({ label: 'Port', value: [site.nearest_port_name, km(site.nearest_port_distance_km)].filter(Boolean).join(' · ') });
    }
  }

  const free = num(site.free_land_ha);
  const flat = num(site.buildable_contiguous_ha);
  const total = num(site.total_area_ha ?? site.areal_ha);
  const need = num(site.plant_required_ha);
  const land = [];
  if (free != null) land.push(`${formatAreaHa(free)} free of buildings`);
  else if (total != null) land.push(formatAreaHa(total));
  if (flat != null) land.push(`${formatAreaHa(flat)} flat`);
  else if (num(site.slope_pct) != null) land.push(`slope ${num(site.slope_pct).toFixed(1)}% at the centre`);
  if (land.length) {
    out.push({ label: 'Land', value: land.join(' · '), note: need != null ? `the plant needs ${formatAreaHa(need)}` : null,
               warn: need != null && flat != null && flat < need });
  }

  const watch = [];
  if (isTrue(site.har_kulturminne)) watch.push('heritage on the property');
  if (isTrue(site.flood_zone)) watch.push('flood zone');
  if (isTrue(site.kvikkleire) || isTrue(site.kvikkleire_hard_stop)) watch.push('quick clay');
  if (isTrue(site.is_salmon_river)) watch.push('salmon river');
  if (isTrue(site.nin_type_a)) watch.push('valuable nature type');
  if (watch.length) out.push({ label: 'Watch', value: watch.join(' · '), warn: true });

  const r = num(site.readiness_score);
  if (r != null) out.push({ label: 'SEIDR Index', value: String(Math.round(r)) });
  return out;
}

// ── substations ──────────────────────────────────────────────────────────
// Properties from GET /layers/grid-connections. cap_* come from the grid
// company's WattApp figures (services/wattapp_sync); queue_mw is Statnett's.
export function substationFacts(p) {
  if (!p) return [];
  const out = [];
  const kv = num(p.voltage_level);
  out.push({ label: 'Voltage', value: kv ? `${kv} kV` : 'not registered' });
  if (p.operator) out.push({ label: 'Operator', value: p.operator });
  const free = num(p.available_cons_mw);
  if (free != null) {
    const age = capacityAge({ updated_at: p.cap_date || null });
    out.push({ label: 'Free capacity', value: `${Math.round(free)} MW for new consumption`,
               note: [p.cap_owner, age.text].filter(Boolean).join(' · '), noteWarn: age.stale });
    const res = num(p.reserved_cons_mw);
    if (res != null) out.push({ label: 'Reserved', value: `${Math.round(res)} MW` });
    const gq = num(p.cap_queue_mw);
    if (gq != null) out.push({ label: 'Queue', value: gq === 0 ? 'none at the grid company' : `${Math.round(gq)} MW at the grid company` });
    const prod = num(p.available_prod_mw);
    if (prod != null) out.push({ label: 'Production', value: `${Math.round(prod)} MW free for production` });
    if (p.cap_stations) out.push({ label: 'Feeders', value: p.cap_stations });
  } else if (!p.country || p.country === 'NO') {
    out.push({ label: 'Free capacity', value: 'not published', note: 'no WattApp station matches this substation' });
  }
  const sq = num(p.queue_mw);
  if (sq != null) out.push({ label: 'Statnett queue', value: sq === 0 ? 'none' : `${Math.round(sq)} MW` });
  const cap = num(p.capacity_mw);
  if (cap) out.push({ label: 'Nameplate', value: `${Math.round(cap)} MW` });
  return out;
}

// ── viewport parcels ─────────────────────────────────────────────────────
// The green/red rule for parcels around the map view. SiteScreener colours
// parcels with verdict.ok; the card lists the reasons, so a red parcel says
// why it is red.
export function parcelVerdict(p, c) {
  const reasons = [];
  if (!p) return { ok: false, reasons: ['no data'] };
  if (p.har_kulturminne === 'true') reasons.push('heritage registered');
  if (p.tvist === 'true') reasons.push('boundary dispute registered');
  if (p.har_forurensning === 'true') reasons.push('polluted ground registered');
  if (p.flood_zone === true) reasons.push('flood zone');
  if (p.landslide_zone === true) reasons.push('landslide zone');
  if (p.kvikkleire_hard_stop === true) reasons.push('quick clay');
  if (p.salmon_river_within_500m === true) reasons.push('salmon river within 500 m');
  if (p.nin_type_a_conflict === true) reasons.push('valuable nature type');
  const suit = p.zone_suitability != null ? parseFloat(p.zone_suitability) : null;
  if (suit === null) reasons.push('zoning not known');
  else if (suit < 0.6) reasons.push(`zoning ${p.zone_name || p.zone_code || ''} does not fit`.replace('  ', ' '));
  if (c) {
    if (p.nearest_grid_distance_km != null && p.nearest_grid_distance_km > c.maxGridDistance)
      reasons.push(`grid ${km(p.nearest_grid_distance_km)}, limit ${km(c.maxGridDistance)}`);
    if (p.nearest_grid_voltage_kv != null && p.nearest_grid_voltage_kv < c.minVoltage)
      reasons.push(`${p.nearest_grid_voltage_kv} kV, needs ${c.minVoltage} kV`);
    if (p.nearest_protected_distance_km != null && p.nearest_protected_distance_km < c.minProtectedDistance)
      reasons.push(`protected area ${km(p.nearest_protected_distance_km)} away, needs ${km(c.minProtectedDistance)}`);
    if (p.nearest_port_distance_km != null && c.maxPortDistance != null && p.nearest_port_distance_km > c.maxPortDistance)
      reasons.push(`port ${km(p.nearest_port_distance_km)}, limit ${km(c.maxPortDistance)}`);
    const areaHa = p.areal_ha != null ? parseFloat(p.areal_ha) : null;
    if (areaHa !== null && areaHa < c.minAreaHa)
      reasons.push(`${formatAreaHa(areaHa)}, needs ${formatAreaHa(c.minAreaHa)}`);
  }
  return { ok: reasons.length === 0, reasons };
}

export function parcelTitle(p) {
  const kommune = p?.kommune && !/^\d+$/.test(String(p.kommune)) ? p.kommune : null;
  const id = p?.matrikkelnummer || (p?.gnr && p?.bnr ? `${p.gnr}/${p.bnr}` : null) || p?.teigid;
  return [kommune, id].filter(Boolean).join(' ') || 'Parcel';
}

export function parcelFacts(p) {
  if (!p) return [];
  const out = [];
  const z = zoningFact(p.zone_code, p.zone_name, p.zone_suitability);
  if (z) out.push(z);
  const ha = num(p.areal_ha);
  if (ha != null) out.push({ label: 'Area', value: formatAreaHa(ha) });
  if (p.nearest_grid_name || p.nearest_grid_distance_km != null) {
    const kv = num(p.nearest_grid_voltage_kv);
    out.push({ label: 'Grid', value: [p.nearest_grid_name, kv ? `${kv} kV` : null, km(p.nearest_grid_distance_km)].filter(Boolean).join(' · ') });
  }
  if (p.nearest_port_name || p.nearest_port_distance_km != null) {
    const depth = num(p.nearest_port_depth_m);
    out.push({ label: 'Port', value: [p.nearest_port_name, km(p.nearest_port_distance_km)].filter(Boolean).join(' · '),
               note: depth != null ? `${depth} m depth` : null });
  }
  if (num(p.nearest_protected_distance_km) != null) out.push({ label: 'Protected area', value: `${km(p.nearest_protected_distance_km)} away` });
  if (num(p.slope_pct) != null) out.push({ label: 'Slope', value: `${num(p.slope_pct).toFixed(1)}% at the centre` });
  const watch = [];
  if (p.kvikkleire_faregrad && !isTrue(p.kvikkleire_hard_stop)) watch.push(`quick clay hazard ${p.kvikkleire_faregrad}`);
  if (p.salmon_river_name && !isTrue(p.salmon_river_within_500m)) watch.push(`salmon river ${p.salmon_river_name}`);
  if (p.nin_type_a_name) watch.push(p.nin_type_a_name);
  if (watch.length) out.push({ label: 'Note', value: watch.join(' · ') });
  return out;
}

// ── rendering ────────────────────────────────────────────────────────────
function Rows({ facts }) {
  return facts.map(f => (
    <div className="hover-card-row" key={f.label}>
      <span className="hover-card-label">{f.label}</span>
      <span className={`hover-card-value${f.warn ? ' is-warn' : ''}`}>
        {f.value}
        {f.note && <span className={`hover-card-note${f.noteWarn ? ' is-warn' : ''}`}>{f.note}</span>}
      </span>
    </div>
  ));
}

export function SiteHoverCard({ site, rank, total, hint = 'Click for the full analysis' }) {
  if (!site) return null;
  const sub = [
    rank != null ? `No. ${rank}${total ? ` of ${total}` : ''}` : null,
    site.result_tier === 'rezoning' ? 'rezoning candidate' : null,
    num(site.constituent_count) > 1 ? `${site.constituent_count} parcels` : null,
  ].filter(Boolean).join(' · ');
  return (
    <div className="hover-card">
      <div className="hover-card-title">{siteTitle(site)}</div>
      {sub && <div className="hover-card-sub">{sub}</div>}
      <Rows facts={siteFacts(site)} />
      {hint && <div className="hover-card-hint">{hint}</div>}
    </div>
  );
}

export function SubstationHoverCard({ properties }) {
  const p = properties || {};
  return (
    <div className="hover-card">
      <div className="hover-card-title">{p.name || 'Substation'}</div>
      <div className="hover-card-sub">Substation{p.country && p.country !== 'NO' ? ` · ${p.country}` : ''}</div>
      <Rows facts={substationFacts(p)} />
    </div>
  );
}

export function ParcelHoverCard({ properties, criteria }) {
  const p = properties || {};
  const v = parcelVerdict(p, criteria);
  return (
    <div className="hover-card">
      <div className="hover-card-title">{parcelTitle(p)}</div>
      <div className={`hover-card-verdict ${v.ok ? 'is-ok' : 'is-fail'}`}>
        {v.ok ? 'Meets the search criteria' : `Outside the search criteria: ${v.reasons.join('; ')}`}
      </div>
      <Rows facts={parcelFacts(p)} />
      <div className="hover-card-hint">Click to analyse this parcel</div>
    </div>
  );
}

// Where to draw a card next to the cursor: above it unless that would leave
// the map, and kept inside the map at the left and right edges.
export function hoverPlacement(x, y, width) {
  const below = y < 320;
  const tx = x < 170 ? '0%' : (width && x > width - 170 ? '-100%' : '-50%');
  return { transform: `translate(${tx}, ${below ? '0' : '-100%'})`, marginTop: below ? 16 : -12 };
}
